import React from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import GallerySectionHeading from "./GallerySectionHeading";
import GalleryLightbox from "./GalleryLightbox";
import useLightbox from "../hooks/useLightbox";
import "../styles/GallerySections.css";
import "../styles/BentoGallerySection.css";

/*
 * Layout 1 — Pinboard Collage (bento-shaped grid, pinned-photo look).
 * Every 6 items, one cell runs large (2x2) and one runs wide (2x1) so
 * the grid doesn't feel like a flat, repetitive table.
 *
 * `seeAllLabel` + `seeAllPath` are optional — pass them when this is
 * used as a homepage teaser section that links out to the full
 * Gallery page; omit them on the Gallery page itself, where this
 * section already *is* the full list.
 */
export default function BentoGallerySection({
  items,
  eyebrow,
  heading,
  sub,
  seeAllLabel,
  seeAllPath,
  id,
}) {
  const { activeIndex, activeItem, open, close, showPrev, showNext } = useLightbox(items);

  const getVariant = (index) => {
    const pos = index % 6;
    if (pos === 0) return "tp-bento__item--large";
    if (pos === 3) return "tp-bento__item--wide";
    return "";
  };

  return (
    <section id={id} className="tp-section tp-gallery tp-gallery--pinboard">
      <div className="tp-container">
        <GallerySectionHeading eyebrow={eyebrow} heading={heading} sub={sub} />

        <div className="tp-bento__grid">
          {items.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={`tp-bento__item ${getVariant(index)}`}
              onClick={() => open(index)}
              aria-label={`View ${item.title} full size`}
            >
              <img src={item.image} alt={item.title} loading="lazy" />
              <span className="tp-bento__overlay">
                <span className="tp-bento__overlay-title">{item.title}</span>
                <span className="tp-bento__zoom">
                  <Icon name="search" size={16} />
                </span>
              </span>
            </button>
          ))}
        </div>

        {seeAllPath && (
          <Link to={seeAllPath} className="tp-testimonial__link">
            {seeAllLabel}
            <Icon name="arrow" size={15} />
          </Link>
        )}
      </div>

      <GalleryLightbox
        items={items}
        activeIndex={activeIndex}
        activeItem={activeItem}
        onClose={close}
        onPrev={showPrev}
        onNext={showNext}
      />
    </section>
  );
}
